/* One uptime monitor: status, window stats, failure + latency charts, latest failures and incidents.
   Range lives in ?range= (24h · 7d · 30d) so a link reopens the same window. */
import { Button } from "@aix/ui"
import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router"
import { ArrowLeft, ExternalLink, RefreshCw } from "lucide-react"
import toast from "react-hot-toast"

import { trpc } from "../../lib/trpc"
import { FailureChart, LatencyChart } from "./UptimeCharts"

enum ERange {
  DAY = "24h",
  WEEK = "7d",
  MONTH = "30d",
}

const RANGE_HOURS: Record<ERange, number> = { [ERange.DAY]: 24, [ERange.WEEK]: 24 * 7, [ERange.MONTH]: 24 * 30 }
const REFETCH_MS = 60_000
const SECOND_MS = 1000

const formatMs = (ms: number | null | undefined) => {
  if (ms === null || ms === undefined) return "—"
  return ms < SECOND_MS ? `${Math.round(ms)}ms` : `${(ms / SECOND_MS).toFixed(1)}s`
}

const formatTime = (value: Date | string) =>
  new Date(value).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })

const formatDuration = (seconds: number) => {
  if (seconds < 60) return `${seconds}s`
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`
  return `${(seconds / 3600).toFixed(1)}h`
}

function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-md border border-border bg-background p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-xl font-semibold tabular-nums">{value}</div>
      {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
    </div>
  )
}

export default function UptimeMonitorDetailScreen() {
  const { monitorId } = useParams({ strict: false }) as { monitorId: string }
  const { range: rawRange } = useSearch({ strict: false }) as { range?: string }
  const range = Object.values(ERange).includes(rawRange as ERange) ? (rawRange as ERange) : ERange.DAY
  const navigate = useNavigate()
  const utils = trpc.useUtils()

  const detail = trpc.uptimeMonitors.getDetail.useQuery(
    { id: monitorId, hours: RANGE_HOURS[range] },
    // skipBatch: batched requests can hang on the Worker's shared Postgres socket (lib/trpc.ts)
    { refetchInterval: REFETCH_MS, keepPreviousData: true, trpc: { context: { skipBatch: true } } },
  )
  const check = trpc.uptimeMonitors.triggerCheck.useMutation({
    onSuccess: (result) => {
      const label = `${result.success ? "Up" : "Down"} · ${result.statusCode ?? "no response"} · ${formatMs(result.latencyMs)}`
      if (result.success) toast.success(label)
      else toast.error(result.error ? `${label} · ${result.error}` : label)
      void utils.uptimeMonitors.getDetail.invalidate({ id: monitorId })
      void utils.uptimeMonitors.list.invalidate()
    },
    onError: (error) => toast.error(error.message || "Check failed"),
  })

  const setRange = (next: ERange) =>
    void navigate({
      to: "/devtools/uptime/$monitorId",
      params: { monitorId },
      search: (prev: Record<string, unknown>) => ({ ...prev, range: next === ERange.DAY ? undefined : next }),
    })

  if (detail.isLoading)
    return (
      <div className="p-8 text-center text-muted-foreground">
        <div className="mb-2 inline-block h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
        <div>Loading…</div>
      </div>
    )
  if (detail.error || !detail.data)
    return (
      <div className="p-8 text-center">
        <p className="text-destructive">{detail.error?.message ?? "Monitor not found"}</p>
        <Link to="/devtools/uptime" className="text-sm underline">
          Back to Uptime Monitors
        </Link>
      </div>
    )

  const { monitor, latestCheck, summary, buckets, recentFailures, incidents } = detail.data
  const isUp = latestCheck?.success ?? null
  const openIncident = incidents.find((incident) => !incident.resolvedAt)

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-6 p-4 lg:p-8">
      <div className="flex flex-col gap-3">
        <Link
          to="/devtools/uptime"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> Uptime Monitors
        </Link>
        <div className="flex flex-wrap items-center gap-3">
          <span
            className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
              isUp === null
                ? "bg-secondary text-muted-foreground"
                : isUp
                  ? "bg-primary/10 text-primary"
                  : "bg-destructive/15 text-destructive"
            }`}
          >
            {isUp === null ? "No checks" : isUp ? "Up" : "Down"}
          </span>
          <h1 className="m-0 text-2xl font-semibold">{monitor.name}</h1>
          {!monitor.enabled && <span className="text-xs text-muted-foreground">paused</span>}
          <div className="ml-auto flex items-center gap-2">
            <Button variant="outline" size="sm" asChild>
              <a href={monitor.url} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="mr-1 h-4 w-4" /> Open URL
              </a>
            </Button>
            <Button size="sm" loading={check.isLoading} onClick={() => check.mutate({ id: monitor.id })}>
              <RefreshCw className="mr-1 h-4 w-4" /> Check now
            </Button>
          </div>
        </div>
        <p className="m-0 break-all text-sm text-muted-foreground">
          {monitor.method} {monitor.url} · {monitor.service} · {monitor.checkType} every {monitor.intervalSeconds}s ·
          expects {monitor.expectedStatus} within {formatMs(monitor.timeoutMs)}
          {latestCheck && ` · last checked ${formatTime(latestCheck.checkedAt)}`}
        </p>
        {!isUp && latestCheck?.error && (
          <p className="m-0 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
            {latestCheck.error}
          </p>
        )}
      </div>

      <div className="flex items-center gap-1" role="tablist" aria-label="Time range">
        {Object.values(ERange).map((value) => (
          <button
            key={value}
            type="button"
            role="tab"
            aria-selected={range === value}
            onClick={() => setRange(value)}
            className={`rounded-md px-3 py-1 text-sm ${
              range === value
                ? "bg-secondary font-medium text-foreground"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {value}
          </button>
        ))}
        {detail.isFetching && <span className="ml-2 text-xs text-muted-foreground">updating…</span>}
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <Stat label={`Uptime ${range}`} value={`${summary.uptimePct}%`} />
        <Stat label="Failed checks" value={String(summary.failed)} sub={`of ${summary.total}`} />
        <Stat label="Latency p50" value={formatMs(summary.p50)} />
        <Stat label="Latency p95" value={formatMs(summary.p95)} />
        <Stat
          label="Incidents"
          value={openIncident ? "Open" : String(incidents.length)}
          sub={openIncident ? `since ${formatTime(openIncident.startedAt)}` : "latest 20"}
        />
      </div>

      <div className="flex flex-col gap-6 rounded-md border border-border p-4">
        <FailureChart buckets={buckets} />
        <LatencyChart buckets={buckets} timeoutMs={monitor.timeoutMs} />
      </div>

      <section className="flex flex-col gap-2">
        <h2 className="m-0 text-lg font-semibold">Latest failures</h2>
        {recentFailures.length ? (
          <div className="overflow-x-auto rounded-md border border-border">
            <table className="w-full text-sm">
              <thead className="bg-secondary text-left text-xs text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 font-medium">When</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                  <th className="px-3 py-2 font-medium">Latency</th>
                  <th className="px-3 py-2 font-medium">Error</th>
                </tr>
              </thead>
              <tbody>
                {recentFailures.map((failure) => (
                  <tr key={failure.id} className="border-t border-border align-top">
                    <td className="whitespace-nowrap px-3 py-2 tabular-nums">{formatTime(failure.checkedAt)}</td>
                    <td className="px-3 py-2 tabular-nums">{failure.statusCode ?? "—"}</td>
                    <td className="px-3 py-2 tabular-nums">{formatMs(failure.latencyMs)}</td>
                    <td className="break-all px-3 py-2 text-muted-foreground">{failure.error ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="m-0 text-sm text-muted-foreground">No failed checks in the last {range}.</p>
        )}
      </section>

      <section className="flex flex-col gap-2">
        <h2 className="m-0 text-lg font-semibold">Incidents</h2>
        {incidents.length ? (
          <ul className="m-0 flex list-none flex-col gap-1 p-0 text-sm">
            {incidents.map((incident) => (
              <li key={incident.id} className="flex flex-wrap gap-x-3 border-b border-border py-1.5">
                <span className="tabular-nums">{formatTime(incident.startedAt)}</span>
                <span className={incident.resolvedAt ? "text-muted-foreground" : "font-medium text-destructive"}>
                  {incident.resolvedAt ? `resolved after ${formatDuration(incident.durationSeconds ?? 0)}` : "ongoing"}
                </span>
                {incident.error && <span className="break-all text-muted-foreground">{incident.error}</span>}
              </li>
            ))}
          </ul>
        ) : (
          <p className="m-0 text-sm text-muted-foreground">No incidents recorded.</p>
        )}
      </section>
    </div>
  )
}
